/**
 * Stock / sellability status for a catalog product combination.
 * - in_stock: buy as normal (no extra UI)
 * - pending_incoming: buy allowed, but order may take longer (show message)
 * - out_of_stock: block buy (same as always)
 */
export type StockStatus = 'in_stock' | 'pending_incoming' | 'out_of_stock';

function parseGarmentControlIds(): number[] {
  const raw = import.meta.env.VITE_GARMENT_CONTROL_IDS;
  if (!raw) return [];
  try {
    // Supports "[1, 2]" or "1,2"
    const normalized = raw.trim().startsWith('[') ? raw : `[${raw}]`;
    const parsed = JSON.parse(normalized);
    return Array.isArray(parsed) ? parsed.map(Number).filter((n) => !Number.isNaN(n)) : [];
  } catch {
    return [];
  }
}

function isOffStockBuysEnabled(): boolean {
  return String(import.meta.env.VITE_OFF_STOCK_BUYS ?? '').toUpperCase() === 'TRUE';
}

/**
 * Resolves if possible to sell for a catalog product.
 *
 * Default (feature off, or garment id not in VITE_GARMENT_CONTROL_IDS):
 *   is_available → in_stock / out_of_stock (unchanged behavior)
 *
 * For controlled garments when VITE_OFF_STOCK_BUYS is true:
 *   1. is_available === true              → in_stock
 *   2. stock_available > 0                → in_stock
 *   3. reserved_by_pending_stock_orders > 0 → pending_incoming
 *   4. otherwise                          → out_of_stock
 */
export function resolveStockStatus(product: {
  garment_id: number;
  is_available?: boolean;
  stock_available?: number | null;
  reserved_by_pending_stock_orders?: number | null;
}): StockStatus {
  const isAvailable = product.is_available ?? true;
  const featureOn = isOffStockBuysEnabled();
  const controlledIds = parseGarmentControlIds();
  // Coerce id in case API sends string garment_id
  const isControlledGarment = featureOn && controlledIds.includes(Number(product.garment_id));

  // Feature off or garment not in allowlist → same as before
  if (!isControlledGarment) {
    return isAvailable ? 'in_stock' : 'out_of_stock';
  }

  // 1. Backend says available → normal sell
  if (isAvailable) {
    return 'in_stock';
  }

  // 2. Not available, but depot still has units → normal sell
  const stock = Number(product.stock_available);
  if (!Number.isNaN(stock) && stock > 0) {
    return 'in_stock';
  }

  // 3. Empty (or unknown) depot, but incoming reserved stock → allow + delay message
  const reserved = product.reserved_by_pending_stock_orders;
  if (reserved != null && !Number.isNaN(Number(reserved)) && Number(reserved) > 0) {
    return 'pending_incoming';
  }

  // 4. No stock and no incoming reserved → out of stock
  return 'out_of_stock';
}

export function isStockStatusBuyable(status: StockStatus | undefined): boolean {
  if (status === undefined) return true;
  return status !== 'out_of_stock';
}
